import { Capacitor, registerPlugin } from "@capacitor/core";

export interface DeviceTrackingReportContext {
  /**
   * true only from an attendance-tablet device with a logged-in user — native stores
   * and POSTs closes ONLY while this is true. Pushed with `false` (and empty
   * credentials) by `syncNativeTrackingContext` on every non-attendance boot / login
   * to disarm a previously armed install; pending closes are never cleared by that.
   */
  enabled: boolean;
  /** Full URL of the device-tracking endpoint. */
  url: string;
  apiKey: string;
  authorization?: string;
  activeRole?: string;
  deviceId: string;
  userId?: string;
  deviceInfo?: Record<string, unknown>;
  batteryInfo?: Record<string, unknown>;
}

/**
 * Native side of device tracking — ANDROID ONLY (attendance tablet).
 *
 * JavaScript is normally the ONLY component that posts to the device-tracking API. The
 * plugin makes the close moment survive a process death so the next launch can report it
 * with its ORIGINAL time, and — when it has the {@link DeviceTrackingReportContext} —
 * also POSTs the close itself at the real close moment:
 *
 *   - Android: `MainActivity.onDestroy()` (not a configuration change) → exit app,
 *     swipe away from recents, the schedule driven auto close and a restart. On top of
 *     that a *tentative* close is stored in `onStop` and dropped again in `onResume`
 *     (covers force stop / OOM kill / reboot / crash — kills without any hook), and
 *     `noteCloseSending` stops native from re-POSTing a close JavaScript is sending.
 *
 * There is NO iOS counterpart any more: the whole feature (auto start / auto close and
 * open / close time tracking) exists for the Android attendance tablet only, so on iOS
 * and on web this plugin is not registered and every call here is a no-op
 * (`isDeviceTrackingNativeAvailable` returns false).
 *
 * One open → one start and one close → at most one request, see
 * `frontend/src/helpers/deviceTracking.ts`.
 */
export interface DeviceTrackingNativePlugin {
  /**
   * A close that the next launch still has to report.
   *
   * `at` prefers the real close (stored in `MainActivity.onDestroy`) and falls back to
   * the *tentative* close stored when the app went to background (the copy that covers a
   * kill without any lifecycle hook — force stop, OOM kill, reboot).
   * `lastReportedAt` is the last close the native side saw confirmed, so the flush can
   * drop a close the server already holds instead of POSTing it a second time.
   * `flushing` (Android) is true while the native next-launch flush is POSTing —
   * JavaScript waits for it instead of sending the same close a second time.
   */
  getPendingClose(): Promise<{
    at: number;
    hasPending: boolean;
    lastReportedAt?: number;
    flushing?: boolean;
  }>;
  /** Clears the pending AND the tentative close (JavaScript owns this close now). */
  clearPendingClose(): Promise<void>;
  /** Tells native a close was just reported, so the same close is not stored again. */
  markCloseReported(options: { at: number }): Promise<void>;
  /**
   * Hands native everything a native close POST needs (so it can send it locally).
   * Called on EVERY app start — it also clears the native "last reported close"
   * marker, so a genuine close shortly after a relaunch is never swallowed by the
   * de-duplication window of the previous session.
   */
  setReportContext(options: DeviceTrackingReportContext): Promise<void>;
  /**
   * Tells native that JavaScript is POSTing a close right now (until `until`), so the
   * native termination hook stores the moment but does NOT fire a second POST for the
   * same close. One close = at most one request, whichever layer wins the race.
   */
  noteCloseSending(options: { until: number }): Promise<void>;
  /**
   * Mirrors a line into the native log (logcat / iOS system log) under the
   * `DeviceTracking` tag. Capacitor forwards the WebView console to logcat on Android
   * but NOT on iOS, so this is how the open / close reports JavaScript sends stay
   * visible on an iPhone / iPad. Fire and forget.
   */
  log(options: { message: string }): Promise<void>;
}

export const DeviceTrackingNative =
  registerPlugin<DeviceTrackingNativePlugin>("DeviceTracking");

/** True only on the native ANDROID app where the DeviceTracking plugin is registered. */
export const isDeviceTrackingNativeAvailable = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("DeviceTracking");

export default DeviceTrackingNative;
